import { Button } from '@shared/components/ui/button';
import { ModalCenter } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { faQrcode } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Location } from '../../models/Location';
import { generateLocationLabelsPdf, LabelSize } from '../../utils/generateLocationLabelsPdf';

interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithLocation: (location: Location) => void;
  openWithLocations: (locations: Location[]) => void;
}

interface LocationLabelModalProps {
  /** Tras generar PDF masivo, p. ej. limpiar selección en la tabla */
  onBulkLabelsGenerated?: () => void;
}

export const LocationLabelModal = forwardRef<ExtendedModalCenterRef, LocationLabelModalProps>(
  ({ onBulkLabelsGenerated }, ref) => {
    const { t } = useTranslation('locationsModule');
    const internalModalRef = useRef<ModalCenterRef>(null);
    const [locations, setLocations] = useState<Location[]>([]);
    const [labelSize, setLabelSize] = useState<LabelSize>('small');
    const [isGenerating, setIsGenerating] = useState(false);

    useImperativeHandle(ref, () => ({
      openModal: () => {
        internalModalRef.current?.openModal();
      },
      closeModal: () => {
        internalModalRef.current?.closeModal();
      },
      openWithLocation: (locationData: Location) => {
        setLocations([locationData]);
        internalModalRef.current?.openModal();
      },
      openWithLocations: (locationList: Location[]) => {
        setLocations(locationList);
        internalModalRef.current?.openModal();
      },
    }));

    const generatePDF = async () => {
      if (locations.length === 0) return;

      setIsGenerating(true);

      try {
        await generateLocationLabelsPdf(locations, labelSize);
        if (locations.length > 1) {
          onBulkLabelsGenerated?.();
        }
      } catch (error) {
        console.error('Error generando PDF:', error);
      } finally {
        setIsGenerating(false);
      }
    };

    const handleCancel = () => {
      internalModalRef.current?.closeModal();
      setLocations([]);
    };

    const isBulk = locations.length > 1;
    const modalTitle = isBulk ? t('modal.label.bulk_title') : t('modal.label.title');

    return (
      <ModalCenter
        ref={internalModalRef}
        title={modalTitle}
        icon={<FontAwesomeIcon icon={faQrcode} className="w-3 h-3" />}
        type="default"
        width="lg"
      >
        {locations.length > 0 && (
          <div className="space-y-6 pt-3 pb-2">
            {isBulk && (
              <p className="text-sm text-muted-foreground">
                {t('modal.label.bulk_count', { count: locations.length })}
              </p>
            )}

            {!isBulk && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{t('table.code')}:</span>
                  <span className="font-medium text-foreground">{locations[0].code}</span>
                </div>
              </div>
            )}

            {isBulk && (
              <div className="max-h-40 overflow-y-auto rounded-md border border-border bg-muted/30 p-3 space-y-1">
                {locations.map((loc) => (
                  <div key={loc.id} className="text-sm text-foreground font-mono">
                    {loc.code}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">{t('modal.label.size_label')}</label>
              <Select value={labelSize} onValueChange={(value: LabelSize) => setLabelSize(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">{t('modal.label.size_small')}</SelectItem>
                  <SelectItem value="large">{t('modal.label.size_large')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-border">
              <Button variant="secondary" onClick={handleCancel}>
                {t('button.cancel')}
              </Button>
              <Button variant="primary" onClick={generatePDF} disabled={isGenerating}>
                {isGenerating ? t('modal.label.generating') : t('modal.label.generate')}
              </Button>
            </div>
          </div>
        )}
      </ModalCenter>
    );
  },
);

LocationLabelModal.displayName = 'LocationLabelModal';
